import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { FiUsers, FiShield, FiBell, FiEdit3 } from "react-icons/fi";
import { LuFileKey2 } from "react-icons/lu";
import { ModuleLayout } from "../../layouts";
import { ContentCard, Loader } from "../../components";
import { StatCard } from "./components";
import { ROUTES_CONSTANTS, QUERY_CONSTANTS } from "../../constants";
import { useAuth } from "../../context";
import { getDashboardStatsApi } from "../../DAL";

const NOTIFICATIONS = [
  { id: 1, title: "Natural Almond Extract", desc: "Certificate of Analysis missing for current batch", color: "text-gray-400" },
  { id: 2, title: "Natural Almond Extract", desc: "Certificate of Analysis missing for current batch", color: "text-yellow-400" },
  { id: 3, title: "Natural Almond Extract", desc: "Certificate of Analysis missing for current batch", color: "text-red-400" },
  { id: 4, title: "Natural Almond Extract", desc: "Certificate of Analysis missing for current batch", color: "text-orange-400" },
  { id: 5, title: "Natural Almond Extract", desc: "Certificate of Analysis missing for current batch", color: "text-green-400" },
  { id: 6, title: "Natural Almond Extract", desc: "Certificate of Analysis missing for current batch", color: "text-orange-400" },
];

const ACTIVITIES = [
  { id: 1, user: "Sarah Chen", item: "Organic Oat Flour", desc: "Updated nutritional specifications about 10 minutes ago" },
  { id: 2, user: "Sarah Chen", item: "Organic Oat Flour", desc: "Updated nutritional specifications about 10 minutes ago" },
  { id: 3, user: "Sarah Chen", item: "Organic Oat Flour", desc: "Updated nutritional specifications about 10 minutes ago" },
  { id: 4, user: "Sarah Chen", item: "Organic Oat Flour", desc: "Updated nutritional specifications about 10 minutes ago" },
  { id: 5, user: "Sarah Chen", item: "Organic Oat Flour", desc: "Updated nutritional specifications about 10 minutes ago" },
  { id: 6, user: "Sarah Chen", item: "Organic Oat Flour", desc: "Updated nutritional specifications about 10 minutes ago" },
];

const CompanyDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const companyId = currentUser?.company?.id;

  const { data: statsData, isLoading } = useQuery({
    queryKey: [QUERY_CONSTANTS.DASHBOARD_STATS, companyId],
    queryFn: () => getDashboardStatsApi(companyId),
    enabled: !!companyId,
  });
  const stats = statsData?.data ?? {};

  const NAV_CARDS = [
    { icon: FiUsers, label: t("dashboard.navUsers"), path: ROUTES_CONSTANTS.USERS },
    { icon: LuFileKey2, label: t("dashboard.navPermissions"), path: ROUTES_CONSTANTS.PERMISSIONS },
    { icon: FiShield, label: t("dashboard.navPermissionGroups"), path: ROUTES_CONSTANTS.PERMISSION_GROUPS },
  ];

  const STATS = [
    { value: stats.company_users_count ?? "—", label: t("dashboard.statUsers") },
    { value: stats.permission_groups_count ?? "—", label: t("dashboard.statPermissionGroups") },
    { value: stats.admins_count ?? "—", label: t("dashboard.statAdmins") },
  ];

  if (isLoading) return <Loader />;

  return (
    <ModuleLayout title={t("companyManagement.title")}>
      {/* Stats */}
      <div className="flex gap-4 mb-4">
        {STATS.map((s) => (
          <StatCard key={s.label} value={s.value} label={s.label} />
        ))}
      </div>

      {/* Nav Cards */}
      <div className="flex gap-4 mb-4">
        {NAV_CARDS.map(({ icon: Icon, label, path }) => (
          <button
            key={label}
            onClick={() => navigate(path)}
            className="flex-1 bg-white border border-[#E3E3E3] rounded-[10px] p-6 flex flex-col items-center gap-3 cursor-pointer hover:bg-gray-50 transition"
          >
            <div className="w-14 h-14 rounded-full bg-[#e8f0fb] flex items-center justify-center">
              <Icon className="w-6 h-6 text-primary-500" />
            </div>
            <span className="text-sm font-medium text-black">{label}</span>
          </button>
        ))}
      </div>

      {/* Bottom panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Smart Notifications */}
        <ContentCard>
          <p className="text-xs font-bold text-[#333] uppercase tracking-wide mb-4">
            {t("dashboard.notifications")}
          </p>
          <div className="divide-y divide-[#f0f0f0]">
            {NOTIFICATIONS.map((n) => (
              <div key={n.id} className="flex items-start gap-3 py-2.5">
                <FiBell className={`w-4 h-4 mt-0.5 flex-shrink-0 ${n.color}`} />
                <div>
                  <p className="text-sm font-medium text-black">{n.title}</p>
                  <p className="text-xs text-gray-400">{n.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </ContentCard>

        {/* Recent Activity Feed */}
        <ContentCard>
          <p className="text-xs font-bold text-[#333] uppercase tracking-wide mb-4">
            {t("dashboard.activityFeed")}
          </p>
          <div className="divide-y divide-[#f0f0f0]">
            {ACTIVITIES.map((a) => (
              <div key={a.id} className="flex items-start gap-3 py-2.5">
                <div className="w-7 h-7 rounded-md bg-[#e8f0fb] flex items-center justify-center flex-shrink-0">
                  <FiEdit3 className="w-3.5 h-3.5 text-primary-500" />
                </div>
                <div>
                  <p className="text-sm text-black">
                    <span className="font-medium">{a.user}</span> updated{" "}
                    <span className="text-primary-500">{a.item}</span>
                  </p>
                  <p className="text-xs text-gray-400">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </ContentCard>
      </div>
    </ModuleLayout>
  );
};

export default CompanyDashboard;
